import React, { useState, useContext } from 'react';
import axios from 'axios';
import { UserContext, WorkspaceContext } from '../../../objects/Context';
import Popup from '../../../components/Popup/Popup';
import './SettingsComponents.css';

function WorkspaceGeneralSettings() {
    const { workspace, setWorkspace } = useContext(WorkspaceContext);
    const { user } = useContext(UserContext);
    const [workspaceName, setWorkspaceName] = useState(workspace.name || '');
    const [testWorkspace, setTestWorkspace] = useState(!!workspace?.test_workspace);

    const [showRemoveConfirm, setShowRemoveConfirm] = useState(false);
    const [confirmText, setConfirmText] = useState('');
    const [removing, setRemoving] = useState(false);
    const [removeError, setRemoveError] = useState('');

    const hasChanges = workspaceName !== (workspace.name || '') || testWorkspace !== !!workspace?.test_workspace;
    const canRemove = confirmText.trim() === (workspace.name || '').trim() && !!workspace.id;

    const handleRemoveWorkspace = async () => {
        if (!canRemove || removing) return;
        setRemoving(true);
        setRemoveError('');
        try {
            await axios.delete(
                `${process.env.REACT_APP_API_BASE_URL}/api/workspaces/delete/${workspace.id}`,
                { withCredentials: true }
            );
            // Everything tied to this workspace is gone — reload so the app
            // re-resolves the user's remaining workspaces from scratch.
            window.location.href = '/';
        } catch (error) {
            console.error('Error removing workspace:', error);
            setRemoveError(error?.response?.data?.error || 'Failed to remove workspace. Please try again.');
            setRemoving(false);
        }
    };

    const closeRemoveConfirm = () => {
        if (removing) return;
        setShowRemoveConfirm(false);
        setConfirmText('');
        setRemoveError('');
    };

    const handleUpdateWorkspace = async () => {
        const updates = {};
        if (workspaceName !== (workspace.name || '')) updates.name = workspaceName;
        if (testWorkspace !== !!workspace?.test_workspace) updates.test_workspace = testWorkspace;
        if (Object.keys(updates).length === 0) return;

        try {
            await axios.put(
                `${process.env.REACT_APP_API_BASE_URL}/api/workspaces/update/${workspace.id}`,
                updates,
                { withCredentials: true }
            );
            setWorkspace(prev => ({ ...prev, ...updates }));
        } catch (error) {
            console.error('Error updating workspace:', error);
        }
    };
    
    return (
        <div className="settings-component">
            <div className="settings-header">
                <div className="settings-title-group">
                    <h1 className="settings-title">Workspace General Settings</h1>
                    <h2 className="settings-subtitle">Update your workspace general settings</h2>
                </div>
            </div>
            <div className="content">
                <div className="item-list">
                    <div className="text-item-label">
                        <div className="title">Workspace Name</div>
                        <div className="description">The name associated with your workspace</div>
                        <input className="text-item-value" type="text" value={workspaceName} onChange={(e) => setWorkspaceName(e.target.value)} placeholder="Enter your workspace name"/>
                    </div>
                    <div className="text-item-label">
                        <div className="title">Contact Email</div>
                        <div className="description">Vairo will use this email for communications about your workspace</div>
                        <input className="text-item-value" type="text" value={user.email} disabled={true} placeholder="Enter your workspace email"/>
                    </div>
                    <label className="checkbox-item">
                        <input
                            type="checkbox"
                            checked={testWorkspace}
                            onChange={(e) => setTestWorkspace(e.target.checked)}
                        />
                        <div className="checkbox-text">
                            <div className="title">Test Workspace</div>
                            <div className="description">Mark this workspace as a test environment. Test workspaces are excluded from usage limits.</div>
                        </div>
                    </label>
                    {/* <div className="privacy-item">
                        <div className="text">
                            <h4>Workspace Access</h4>
                            <p>{accessType === 'private' ? 'PRIVATE - Visible to only you' : 'TEAM-WIDE - Visible to all members in your organization'}</p>
                        </div>
                        <div className={`toggle ${accessType === 'private' ? 'private' : 'team-wide'}`} onClick={() => setAccessType(accessType === 'private' ? 'team-wide' : 'private')}>
                            <div className='slider'></div>
                        </div>
                    </div> */}
                </div>
                <div className="button-container">
                    <button className="save-button" disabled={!hasChanges} onClick={handleUpdateWorkspace}>
                        Update Workspace
                    </button>
                </div>

                {!workspace.isSandbox && (
                    <div className="danger-zone">
                        <div className="danger-zone-text">
                            <div className="title">Remove Workspace</div>
                            <div className="description">
                                Permanently delete this workspace and everything in it. This action cannot be undone.
                            </div>
                        </div>
                        <button className="remove-workspace-button" onClick={() => setShowRemoveConfirm(true)}>
                            Remove Workspace
                        </button>
                    </div>
                )}
            </div>

            {showRemoveConfirm && (
                <Popup
                    onClose={closeRemoveConfirm}
                    content={
                        <div className="remove-workspace-popup">
                            <h3>Remove workspace</h3>
                            <p>
                                Are you sure? This permanently deletes <strong>{workspace.name}</strong> and
                                all of its forecasts, data sources, connected accounts, synced data, and chat
                                history. Everyone's access to this workspace will be removed. This action cannot be undone.
                            </p>
                            <p className="confirm-instruction">
                                Type <strong>{workspace.name}</strong> to confirm.
                            </p>
                            <input
                                className="confirm-input"
                                type="text"
                                value={confirmText}
                                onChange={(e) => setConfirmText(e.target.value)}
                                placeholder={workspace.name}
                                disabled={removing}
                                autoFocus
                            />
                            {removeError && <div className="remove-error">{removeError}</div>}
                            <div className="remove-workspace-actions">
                                <button className="cancel-button" onClick={closeRemoveConfirm} disabled={removing}>
                                    Cancel
                                </button>
                                <button
                                    className="confirm-remove-button"
                                    onClick={handleRemoveWorkspace}
                                    disabled={!canRemove || removing}
                                >
                                    {removing ? 'Removing...' : 'Remove Workspace'}
                                </button>
                            </div>
                        </div>
                    }
                />
            )}
        </div>
    );
}

export default WorkspaceGeneralSettings;